<script lang="ts">
	import SkeletonBlock from '$lib/components/loading/SkeletonBlock.svelte'
	import { randomWidth } from '$lib/utils/loading'
	import { isMobile } from '$src/lib/utils/isMobile'
	import OrganizerLoading from './OrganizerLoading.svelte'
	import { urlState } from '$lib/stores/url-state'

	interface Props {
		vertical?: boolean
	}

	let isFullScreen = $derived($urlState.isFullScreen ?? false)

	let { vertical = false }: Props = $props()
</script>

<div class="relative">
	{#if vertical}
		<div
			class="flex flex-col bg-brand-gray-5 items-center overflow-hidden max-w-sm w-full shadow-lg mx-auto animate-pulse"
		>
			<div class="relative flex-shrink-0 w-full">
				<div class="h-52 w-full overflow-hidden flex-shrink-0 bg-brand-gray-3">
					<SkeletonBlock width="100%" height="100%" />
				</div>
				<div
					class="h-12 w-12 rounded-full bg-brand-gray-3 ring-4 ring-offset-0 ring-brand-gray-5 border border-brand-gray-2 absolute -bottom-3 left-4"
				>
					<!-- spacer -->
				</div>
			</div>

			<div class="p-4 pt-5 flex flex-col text-start w-full">
				<SkeletonBlock width={`${randomWidth(30, 55)}%`} height="16px" class="mb-2" />
				<SkeletonBlock width={`${randomWidth(70, 85)}%`} height="12px" class="mb-3" />

				<div class="flex items-center space-x-2 mt-3">
					<SkeletonBlock width="16px" height="16px" />
					<SkeletonBlock width="16px" height="16px" />
					<SkeletonBlock width="16px" height="16px" />
					<SkeletonBlock width="16px" height="16px" />
				</div>
			</div>
		</div>
	{:else if isMobile() || !isFullScreen}
		<div class="flex flex-col items-start w-full animate-pulse">
			<div class="px-4 mt-1 w-60">
				<SkeletonBlock width={`${randomWidth(50, 70)}%`} height="22px" class="mb-2" />
			</div>
			<div class="relative mt-[6px] w-full overflow-x-hidden">
				<div class="flex gap-x-1">
					<div class="ml-3"></div>
					{#each Array(5) as _, i}
						<SkeletonBlock width="140px" height="200px" borderRadius="8px" class="flex-shrink-0" />
					{/each}
					<div class="mr-3"></div>
				</div>
			</div>

			<div class="px-4 mt-4 w-full">
				<OrganizerLoading />
				<SkeletonBlock width={`${randomWidth(80, 90)}%`} height="16px" class="mb-4 mt-2" />

				<p class="text-xs text-brand-gray-4 mb-1">Estimated Trip Cost</p>
				<div class="flex mb-4 divide-x divide-brand-gray-4 [&>*]:px-4 [&>*:first-child]:pl-0">
					{#each ['Weekend', '7 Days', '14 Days'] as period}
						<div class="flex flex-col">
							<span class="text-xs text-brand-gray-4">{period}</span>
							<SkeletonBlock width="60px" height="14px" class="mt-1" />
						</div>
					{/each}
				</div>

				<div class="flex justify-between w-full">
					<div class="flex items-center space-x-4">
						<div class="flex space-x-2">
							<SkeletonBlock width="16px" height="16px" />
							<SkeletonBlock width="16px" height="16px" />
							<SkeletonBlock width="16px" height="16px" />
							<SkeletonBlock width="16px" height="16px" />
						</div>
					</div>
					<SkeletonBlock width="60px" height="14px" />
				</div>
			</div>
		</div>
	{:else}
		<div class="flex items-center w-full px-4 animate-pulse">
			<div class="flex items-center w-full max-w-full">
				<div class="relative flex-shrink-0">
					<div class="h-[200px] w-[140px] rounded-lg bg-brand-gray-3">
						<SkeletonBlock width="100%" height="100%" borderRadius="8px" />
					</div>
					<div
						class="h-12 w-12 absolute -bottom-2 -right-2 rounded-full bg-brand-gray-3 ring-4 ring-offset-0 ring-brand-gray-5 border border-brand-gray-2"
					>
						<!-- spacer -->
					</div>
				</div>

				<div class="pl-4 h-[200px] flex flex-col text-start justify-between w-full">
					<div class="flex flex-col w-full">
						<SkeletonBlock width={`${randomWidth(30, 55)}%`} height="24px" class="mb-2" />
						<SkeletonBlock width={`${randomWidth(70, 85)}%`} height="16px" class="mb-4" />

						<div class="flex flex-wrap gap-2 my-2">
							{#each Array(3) as _}
								<SkeletonBlock width="80px" height="24px" borderRadius="9999px" />
							{/each}
						</div>
					</div>

					<div class="flex flex-col w-full">
						<p class="text-xs text-brand-gray-4 mb-1">Estimated Trip Cost</p>
						<div
							class="flex mt-1 mb-4 divide-x divide-brand-gray-4 [&>*]:px-4 [&>*:first-child]:pl-0"
						>
							{#each ['Weekend', '7 Days', '14 Days'] as period}
								<div class="flex flex-col">
									<span class="text-xs text-brand-gray-4">{period}</span>
									<SkeletonBlock width="60px" height="14px" class="mt-1" />
								</div>
							{/each}
						</div>

						<div class="flex justify-between w-full">
							<div class="flex items-center space-x-4">
								<div class="flex space-x-2">
									<SkeletonBlock width="16px" height="16px" />
									<SkeletonBlock width="16px" height="16px" />
									<SkeletonBlock width="16px" height="16px" />
									<SkeletonBlock width="16px" height="16px" />
								</div>
							</div>
							<SkeletonBlock width="60px" height="14px" />
						</div>
					</div>
				</div>
			</div>
		</div>
	{/if}
</div>
